import { LitElement, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "ui-components/button/wow-button";
import "ui-components/form-elements/input/wow-input";
import "ui-components/form-elements/textarea/textarea";
import "ui-components/form-elements/select/wow-select";
import "ui-components/media-uploader/media-uploader";
import "ui-components/modal/wow-modal";
import { NotificationService } from "ui-components/notification";

import { styles } from "./create-live-event-modal.css";
import { rpc } from "ui-common/services/rpc";
import type { CreateLiveEventParams } from "api-contracts/rpc/create-live-event";
import { t, translate } from "../../translations/i18n";

const MAX_ATTENDEES_LIMIT = 10000;
const MAX_PRICE_LIMIT = 999;

@customElement("create-live-event-modal")
export class CreateLiveEventModal extends LitElement {
  static override styles = [styles];

  @property({ type: Array })
  featureFlags: string[] = [];

  @state()
  private eventTitle: string = "";

  @state()
  private description = "";

  @state()
  private contentTier: "free" | "premium" = "premium";

  @state()
  private feeEuros = "";

  @state()
  private scheduledDate = "";

  @state()
  private scheduledTime = "";

  @state()
  private maxAttendees = "";

  @state()
  private thumbnailFileId?: string;

  @state()
  private thumbnailUrl?: string;

  @state()
  private whatsappUrl = "";

  @state()
  private postBuyingVideoUrl = "";

  @state()
  private postBuyingVideoFileId?: string;

  @state()
  private ctaText = "";

  @state()
  private backgroundImageFileIds: string[] = [];

  @state()
  private backgroundImageUrls: string[] = [];

  @state()
  private isSubmitting = false;

  @state()
  private errors: Record<string, string> = {};

  private hasInitializedContentTier = false;

  constructor() {
    super();
    this.eventTitle = "";
    this.description = "";
    this.feeEuros = "";
    this.maxAttendees = "";
    this.scheduledDate = "";
    this.scheduledTime = "";
    this.whatsappUrl = "";
    this.errors = {};

    this.addEventListener("footer-button-clicked", (e) => {
      console.log("EEE: ", e);
    });
  }

  protected override updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);
    if (changedProperties.has("featureFlags") && !this.hasInitializedContentTier && this.featureFlags.length > 0) {
      this.contentTier = this.defaultContentTier;
      this.hasInitializedContentTier = true;
    }
  }

  private validateForm(): boolean {
    const newErrors: Record<string, string> = {};

    if (!this.eventTitle || !this.eventTitle.trim()) {
      newErrors.title = translate("createLiveEventsModal.titleRequired");
    }

    if (!this.description || !this.description.trim()) {
      newErrors.description = translate("createLiveEventsModal.descriptionRequired");
    }

    if (!this.scheduledDate) {
      newErrors.date = translate("createLiveEventsModal.dateRequired");
    }

    if (!this.scheduledTime) {
      newErrors.time = translate("createLiveEventsModal.timeRequired");
    }

    if (this.scheduledDate && this.scheduledTime) {
      const scheduledDateTime = new Date(`${this.scheduledDate}T${this.scheduledTime}`);
      if (scheduledDateTime <= new Date()) {
        newErrors.datetime = translate("createLiveEventsModal.dateMustBeFuture");
      }
    }

    if (this.maxAttendees && this.maxAttendees.trim() !== "") {
      const numValue = Number(this.maxAttendees);
      if (isNaN(numValue) || numValue < 1) {
        newErrors.maxAttendees = translate("createLiveEventsModal.maxAttendeesMustBePositive");
      }

      if (numValue > MAX_ATTENDEES_LIMIT) {
        newErrors.maxAttendees = "Max allowed: 10000";
      }
    }

    // Ticket price is required for premium events (always required if user only has paid-live-events)
    if (this.contentTier === "premium") {
      if (!this.feeEuros || !this.feeEuros.trim()) {
        newErrors.fee = translate("createLiveEventsModal.priceRequired");
      } else {
        const numValue = parseFloat(this.feeEuros);
        if (isNaN(numValue) || numValue <= 0) {
          newErrors.fee = translate("createLiveEventsModal.pricePositive");
        } else if (numValue > MAX_PRICE_LIMIT) {
          newErrors.fee = translate("createLiveEventsModal.priceLimit");
        }
      }
    }

    this.errors = newErrors;
    return Object.keys(newErrors).length === 0;
  }

  private async handleSubmit() {
    if (!this.validateForm()) {
      return;
    }

    this.isSubmitting = true;

    try {
      // Parse as local time to validate
      const scheduledDateTime = new Date(`${this.scheduledDate}T${this.scheduledTime}`);

      if (isNaN(scheduledDateTime.getTime())) {
        NotificationService.error("Invalid date or time format");
        this.isSubmitting = false;
        return;
      }

      if (scheduledDateTime <= new Date()) {
        NotificationService.error("Scheduled time must be in the future");
        this.isSubmitting = false;
        return;
      }

      const localDateTime = new Date(`${this.scheduledDate}T${this.scheduledTime}:00`);
      const isoString = localDateTime.toISOString();

      const params: CreateLiveEventParams = {
        title: (this.eventTitle || "").trim(),
        description: (this.description || "").trim(),
        content_tier: this.contentTier,
        fee_cents: this.contentTier === "premium" ? Math.round(parseFloat(this.feeEuros) * 100) : undefined,
        scheduled_start_time: isoString,
        max_attendees: this.maxAttendees ? Number(this.maxAttendees) : undefined,
        thumbnail_file_id: this.thumbnailFileId,
        whatsapp_url: this.whatsappUrl?.trim() || undefined,
        post_buying_video_file_id: this.postBuyingVideoFileId,
        cta_text: this.ctaText?.trim() || undefined,
        background_image_file_ids: this.backgroundImageFileIds.length > 0 ? this.backgroundImageFileIds : undefined,
      };

      const result = await rpc("create-live-event", params);

      if (result && typeof result === "object" && "success" in result && result.success) {
        NotificationService.success("Live event created successfully!");

        // Dispatch event to refresh the list
        this.dispatchEvent(
          new CustomEvent("event-created", {
            detail: { event: (result as { event?: unknown }).event },
            bubbles: true,
          }),
        );

        // Close modal after a short delay to allow notification to be seen
        setTimeout(() => {
          this.handleClose();
        }, 500);
      } else {
        const message =
          result && typeof result === "object" && "message" in result
            ? (result as { message?: string }).message || "Failed to create live event"
            : "Failed to create live event";
        NotificationService.error(message);
      }
    } catch {
      NotificationService.error("Failed to create live event");
    } finally {
      this.isSubmitting = false;
    }
  }

  private handleClose() {
    // Reset form when closing
    this.resetForm();
    this.dispatchEvent(new CustomEvent("modal-close", { bubbles: true }));
  }

  private get hasBothLiveEventFlags(): boolean {
    return this.featureFlags.includes("open-live-events") && this.featureFlags.includes("paid-live-events");
  }

  private get hasBackgroundImagesFlag(): boolean {
    return this.featureFlags.includes("live-event-background-images");
  }

  private get defaultContentTier(): "free" | "premium" {
    if (this.featureFlags.includes("paid-live-events")) {
      return "premium";
    }
    return "free";
  }

  private get todayDate(): string {
    return new Date().toISOString().split("T")[0]!;
  }

  private resetForm() {
    this.eventTitle = "";
    this.description = "";
    this.contentTier = this.defaultContentTier;
    this.feeEuros = "";
    this.scheduledDate = "";
    this.scheduledTime = "";
    this.maxAttendees = "";
    this.thumbnailFileId = undefined;
    this.thumbnailUrl = undefined;
    this.whatsappUrl = "";
    this.postBuyingVideoUrl = "";
    this.postBuyingVideoFileId = undefined;
    this.ctaText = "";
    this.backgroundImageFileIds = [];
    this.backgroundImageUrls = [];
    this.isSubmitting = false;
    this.errors = {};
  }

  private handleThumbnailUpload(e: CustomEvent) {
    const { fileId } = e.detail;
    this.thumbnailFileId = fileId;
  }

  private handleVideoUpload(e: CustomEvent) {
    const { fileId } = e.detail;
    this.postBuyingVideoFileId = fileId;
  }

  private handleBackgroundImageUpload(index: number, e: CustomEvent) {
    const { fileId, fileUrl } = e.detail;
    const newFileIds = [...this.backgroundImageFileIds];
    const newUrls = [...this.backgroundImageUrls];

    if (fileId) {
      newFileIds[index] = fileId;
      newUrls[index] = fileUrl || "";
    } else {
      newFileIds.splice(index, 1);
      newUrls.splice(index, 1);
    }

    this.backgroundImageFileIds = newFileIds.filter(Boolean);
    this.backgroundImageUrls = newUrls.filter(Boolean);
  }

  private handleFeeInput(e: Event) {
    const input = e.target as HTMLInputElement;
    this.feeEuros = input.value;

    this.errors = { ...this.errors, fee: "" };
  }

  protected override render() {
    const submitText = this.isSubmitting
      ? t("createLiveEventsModal.creating")
      : t("createLiveEventsModal.submitButton");

    return html`
      <wow-modal
        mobile-fullscreen
        ?disable-primary-button=${this.isSubmitting}
        @close=${this.handleClose}
        @footer-button-clicked=${this.handleSubmit}
      >
        <span slot="header">${t("createLiveEventsModal.header")}</span>

        <div class="form-content">
          <!-- Event Cover Image -->
          <media-uploader
            responsive
            context="cover_image"
            label="${t("createLiveEventsModal.firstThumb")}"
            subtitle="${t("createLiveEventsModal.firstThumbSubtitle")}"
            viewport="cover"
            currentFileUrl=${this.thumbnailUrl}
            @change=${this.handleThumbnailUpload}
          ></media-uploader>

          <!-- Title -->
          <wow-input
            label="${t("createLiveEventsModal.title")}"
            placeholder="${t("createLiveEventsModal.titlePlaceholder")}"
            .value=${this.eventTitle || ""}
            @input=${(e: Event) => {
              this.eventTitle = (e.target as HTMLInputElement).value || "";
              this.errors = { ...this.errors, title: "" };
            }}
            ?error=${!!this.errors.title}
            help-text=${this.errors.title || t("createLiveEventsModal.titleHelper")}
            required
          ></wow-input>

          <!-- Description -->
          <wow-textarea
            label="${t("createLiveEventsModal.description")}"
            placeholder="${t("createLiveEventsModal.descriptionPlaceholder")}"
            .value=${this.description || ""}
            @input=${(e: Event) => {
              this.description = (e.target as HTMLTextAreaElement).value || "";
              this.errors = { ...this.errors, description: "" };
            }}
            ?error=${!!this.errors.description}
            help-text=${this.errors.description || t("createLiveEventsModal.descriptionHelper")}
            rows="3"
            maxlength="750"
            autogrow
            required
          ></wow-textarea>

          <!-- Access Level - only show if user has both flags -->
          ${this.hasBothLiveEventFlags
            ? html`
                <wow-select
                  label="${t("createLiveEventsModal.accessLevel")}"
                  help-text=${t("createLiveEventsModal.accessLevelHelper")}
                  .options=${[
                    { value: "free", label: "Free - Anyone can attend" },
                    { value: "premium", label: "Premium - Ticket required" },
                  ]}
                  .value=${this.contentTier}
                  @change=${(e: CustomEvent) => {
                    this.contentTier = e.detail.value;
                    this.errors = { ...this.errors, fee: "" };
                  }}
                ></wow-select>
              `
            : nothing}

          <!-- Ticket Price - only show for premium events -->
          ${this.contentTier === "premium"
            ? html`
                <wow-input
                  type="number"
                  inputmode="decimal"
                  label="${t("createLiveEventsModal.price")}"
                  placeholder="${t("createLiveEventsModal.pricePlaceholder")}"
                  help-text=${this.errors.fee || t("createLiveEventsModal.priceHelper")}
                  min="0.50"
                  max="${MAX_PRICE_LIMIT}"
                  step="0.01"
                  .value=${this.feeEuros}
                  @input=${this.handleFeeInput}
                  ?error=${!!this.errors.fee}
                  required
                >
                </wow-input>
              `
            : nothing}

          <!-- Date and Time -->
          <div class="form-row">
            <wow-input
              type="date"
              label="${t("createLiveEventsModal.date")}"
              .value=${this.scheduledDate}
              min=${this.todayDate}
              @input=${(e: Event) => {
                this.scheduledDate = (e.target as HTMLInputElement).value;
                this.errors = { ...this.errors, date: "", datetime: "" };
              }}
              ?error=${!!this.errors.date}
              help-text=${this.errors.date || t("createLiveEventsModal.dateHelper")}
              required
            ></wow-input>

            <wow-input
              type="time"
              label="${t("createLiveEventsModal.time")}"
              .value=${this.scheduledTime}
              @input=${(e: Event) => {
                this.scheduledTime = (e.target as HTMLInputElement).value;
                this.errors = { ...this.errors, time: "", datetime: "" };
              }}
              ?error=${!!this.errors.time}
              help-text=${this.errors.time || t("createLiveEventsModal.dateHelper")}
              required
            ></wow-input>
          </div>

          ${this.errors.datetime ? html` <div class="error-message">${this.errors.datetime}</div> ` : nothing}

          <!-- Max Attendees -->
          <wow-input
            type="number"
            inputmode="numeric"
            label="${t("createLiveEventsModal.maxAttendees")}"
            placeholder="${t("createLiveEventsModal.maxAttendeesPlaceholder")}"
            help-text=${t("createLiveEventsModal.maxAttendeesHelper")}
            .value=${this.maxAttendees}
            step="1"
            max="${MAX_ATTENDEES_LIMIT}"
            @input=${(e: InputEvent) => {
              const input = e.target as HTMLInputElement;
              const value = input.value.replace(/[^0-9]/g, "");
              input.value = value;
              this.maxAttendees = value;
              this.errors = { ...this.errors, maxAttendees: "" };
            }}
            @keydown=${(e: KeyboardEvent) => {
              if (
                !/[0-9]/.test(e.key) &&
                e.key !== "Backspace" &&
                e.key !== "Delete" &&
                e.key !== "ArrowLeft" &&
                e.key !== "ArrowRight"
              ) {
                e.preventDefault();
              }
            }}
            ?error=${!!this.errors.maxAttendees}
          ></wow-input>

          <!-- WhatsApp URL -->
          <wow-input
            type="url"
            label="${t("createLiveEventsModal.whatsApp")}"
            placeholder="https://chat.whatsapp.com/..."
            help-text=${t("createLiveEventsModal.whatsAppHelper")}
            .value=${this.whatsappUrl}
            @input=${(e: Event) => {
              this.whatsappUrl = (e.target as HTMLInputElement).value;
            }}
          ></wow-input>

          <!-- Post-Purchase Media -->
          <media-uploader
            responsive
            context="cover_image"
            label="${t("createLiveEventsModal.secondThumb")}"
            subtitle=${t("createLiveEventsModal.secondThumbSubtitle")}
            viewport="post"
            currentFileUrl=${this.postBuyingVideoUrl}
            @change=${this.handleVideoUpload}
          ></media-uploader>

          <!-- CTA Text -->
          <wow-input
            label="${t("createLiveEventsModal.ctaButton")}"
            placeholder="${t("createLiveEventsModal.ctaButtonPlaceholder")}"
            .value=${this.ctaText}
            @input=${(e: Event) => {
              this.ctaText = (e.target as HTMLInputElement).value;
            }}
            maxlength="255"
          ></wow-input>

          <!-- Background Images -->
          ${this.hasBackgroundImagesFlag
            ? html`
                <div class="background-images-section">
                  <label class="section-label">${t("createLiveEventsModal.bgImage")}</label>
                  <div class="background-images-grid">
                    ${[0, 1, 2, 3].map(
                      (index) => html`
                        <media-uploader
                          context="cover_image"
                          viewport="post"
                          currentFileUrl=${this.backgroundImageUrls[index] || ""}
                          @change=${(e: CustomEvent) => this.handleBackgroundImageUpload(index, e)}
                        ></media-uploader>
                      `,
                    )}
                  </div>
                </div>
              `
            : nothing}
        </div>

        <span slot="footer-button-text">${submitText}</span>
      </wow-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "create-live-event-modal": CreateLiveEventModal;
  }
}
